import { BookOpenIcon, DollarSignIcon, HashIcon, TerminalIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslate } from "@/utils/i18n";

interface MemoEmptyStateProps {
  readonly?: boolean;
}

type FeatureType = "tags" | "tickers" | "slash_commands";

const FEATURE_CONFIG = {
  tags: {
    icon: HashIcon,
    color: "blue",
    gradient: "from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30",
    borderColor: "border-blue-200/50 dark:border-blue-800/30",
    iconColor: "text-blue-600 dark:text-blue-400",
    bgClass: "bg-blue-100 text-blue-600 dark:bg-blue-950/30 dark:text-blue-400",
  },
  tickers: {
    icon: DollarSignIcon,
    color: "green",
    gradient: "from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30",
    borderColor: "border-green-200/50 dark:border-green-800/30",
    iconColor: "text-green-600 dark:text-green-400",
    bgClass: "bg-green-100 text-green-600 dark:bg-green-950/30 dark:text-green-400",
  },
  slash_commands: {
    icon: TerminalIcon,
    color: "purple",
    gradient: "from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30",
    borderColor: "border-purple-200/50 dark:border-purple-800/30",
    iconColor: "text-purple-600 dark:text-purple-400",
    bgClass: "bg-purple-100 text-purple-600 dark:bg-purple-950/30 dark:text-purple-400",
  },
};

export const MemoEmptyState = ({ readonly = false }: MemoEmptyStateProps) => {
  const t = useTranslate();

  // Get feature configuration
  const features: FeatureType[] = ["tags", "tickers", "slash_commands"];

  return (
    <div className="w-full py-12 px-6 flex flex-col items-center justify-center">
      {/* Animated background pattern */}
      <div className="relative mb-8">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-amber-500/5 rounded-full blur-3xl" />

        {/* Central icon container */}
        <div className="relative">
          {/* Outer ring with slow rotation */}
          <div
            className="absolute inset-0 -m-4 rounded-full border border-dashed border-purple-500/20"
            style={{ animation: "spin 20s linear infinite" }}
          />

          {/* Inner ring with counter-rotation */}
          <div
            className="absolute inset-0 -m-2 rounded-full border border-dotted border-amber-500/20"
            style={{ animation: "spin 15s linear infinite reverse" }}
          />

          {/* Core icon */}
          <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-50 to-amber-50 dark:from-purple-950/30 dark:to-amber-950/30 border border-purple-200/50 dark:border-purple-800/30 flex items-center justify-center shadow-lg shadow-purple-500/10">
            <BookOpenIcon className="w-10 h-10 text-purple-600 dark:text-purple-500" />
          </div>

          {/* Floating particles */}
          <div className="absolute -top-2 -right-2 w-3 h-3 rounded-full bg-purple-400/60 animate-pulse" />
          <div className="absolute -bottom-1 -left-3 w-2 h-2 rounded-full bg-amber-400/60 animate-pulse delay-300" />
        </div>
      </div>

      {/* Main message */}
      <div className="text-center mb-8 max-w-md">
        <h3 className="text-lg font-semibold text-foreground mb-2">{t("memoEmptyState.title")}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{t("memoEmptyState.description")}</p>
      </div>

      {/* Feature cards */}
      {!readonly && (
        <div className="w-full max-w-lg mb-8">
          <p className="text-xs font-medium text-muted-foreground mb-3 text-center">{t("memoEmptyState.explore_features")}</p>
          <div className="grid grid-cols-1 gap-2">
            {features.map((featureType, index) => {
              const config = FEATURE_CONFIG[featureType];
              const Icon = config.icon;

              return (
                <div
                  key={featureType}
                  className={cn(
                    "group relative flex items-start gap-3 p-3 rounded-lg",
                    "border border-transparent",
                    "bg-muted/30 hover:bg-muted/60",
                    "transition-all duration-200",
                    "hover:shadow-md hover:shadow-purple-500/5",
                    "hover:border-purple-500/20",
                    "cursor-default",
                  )}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center transition-colors", config.bgClass)}>
                      <Icon className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground mb-1">{t(`memoEmptyState.features.${featureType}.title`)}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {t(`memoEmptyState.features.${featureType}.description`)}
                    </p>
                  </div>
                  {/* Example tag */}
                  <div className="flex-shrink-0">
                    <div
                      className={cn(
                        "px-2 py-1 rounded text-xs font-mono",
                        "bg-background border",
                        featureType === "tags" && "text-blue-600 border-blue-200 dark:text-blue-400 dark:border-blue-800",
                        featureType === "tickers" && "text-green-600 border-green-200 dark:text-green-400 dark:border-green-800",
                        featureType === "slash_commands" && "text-purple-600 border-purple-200 dark:text-purple-400 dark:border-purple-800",
                      )}
                    >
                      {t(`memoEmptyState.features.${featureType}.example`)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Helper text */}
      <p className="mt-6 text-xs text-muted-foreground">{t("memoEmptyState.helper_text")}</p>
    </div>
  );
};

export default MemoEmptyState;
